import {
  forwardRef,
  startTransition,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  Separator,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { RiftboundPlayerAvatar } from '../RiftboundPlayerAvatar';
import { RiftboundMenuItemButton } from '../ui/menu-item-button';
import { RiftboundPopover } from '../ui/popover';
import { SlugText } from '../SlugText';
import {
  MdAutoStories,
  MdCollections,
  MdDownload,
  MdHome,
  MdLogout,
  MdMailOutline,
  MdOutlineCollections,
  MdOutlineHome,
  MdOutlinePerson,
  MdOutlinePrint,
  MdOutlineSettings,
  MdOutlineSportsScore,
  MdPerson,
  MdPhonelinkSetup,
  MdPrint,
  MdSettings,
  MdSportsScore,
  MdGroups,
  MdOutlineGroups,
} from 'react-icons/md';
import { useAuth } from '../../hooks/useAuth';
import { useCollectionSummary } from '../../hooks/useCollectionSummary';
import { useUserDecks } from '../../hooks/useDeck';
import { useBinders } from '../../hooks/useBinders';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { JoinRequestsModal } from '../../features/teams/components/JoinRequestsModal';
import { useAdminPendingJoinRequests } from '../../features/teams/hooks/useAdminPendingJoinRequests';
import { usePendingInvites } from '../../features/teams/hooks/useTeamInvites';
import { useUserTeam } from '../../features/teams/hooks/useUserTeam';
import { MobileFilterFabBadge } from './MobileFilterFabBadge';
import DeckIcon from '../../images/deck-icon.svg?react';
import { RiftboundBottomSheet, SHEET_Z_FILTER } from '../ui/bottom-sheet';
import { playerProfilePath } from '../../utils/profilePaths';

// ─── Stat box ─────────────────────────────────────────────────────────────────

function StatBox({ label, value }: { label: string; value: number }) {
  return (
    <Box flex={1} textAlign="center" py={{ base: 2, md: 2.5 }} px={2}>
      <Text
        fontWeight="bold"
        fontSize={{ base: 'md', md: 'lg' }}
        color="accent"
        lineHeight={1}
      >
        {value}
      </Text>
      <Text
        fontSize="2xs"
        color="textMuted"
        textTransform="uppercase"
        letterSpacing="wider"
        mt={0.5}
      >
        {label}
      </Text>
    </Box>
  );
}

// ─── Menu item ────────────────────────────────────────────────────────────────

interface MenuItemProps {
  icon: ReactNode;
  iconActive?: ReactNode;
  label: string;
  shortcut?: string;
  /** Accent notice dot over the icon (e.g. pending join requests). */
  badgeCount?: number;
  isActive?: boolean;
  to?: string;
  onClick?: (e: MouseEvent) => void;
  danger?: boolean;
  /** Visible only below the `md` breakpoint. */
  mobileOnly?: boolean;
  /** Hidden below the `md` breakpoint. */
  hideOnMobile?: boolean;
  /**
   * Mobile sheet density. `primary` = large nav rows; `secondary` = quieter
   * settings/actions. Desktop always stays compact.
   */
  tone?: 'primary' | 'secondary';
}

function MenuItem({
  icon,
  iconActive,
  label,
  shortcut,
  badgeCount,
  isActive,
  to,
  onClick,
  danger,
  mobileOnly,
  hideOnMobile,
  tone = 'primary',
}: MenuItemProps) {
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const renderedIcon = isActive && iconActive ? iconActive : icon;
  const mobileNav = isMobile && tone === 'primary';
  const mobileSecondary = isMobile && tone === 'secondary';
  // Mobile sheet: no accent bordered pills. Desktop keeps compact selected chrome.
  const showSelectedChrome = !isMobile && Boolean(isActive);

  const item = (
    <RiftboundMenuItemButton
      showCheck={false}
      as={to ? RouterLink : 'button'}
      {...(to ? { to } : {})}
      selected={showSelectedChrome}
      cursor="pointer"
      color={
        danger
          ? { base: 'red.600', _dark: 'red.400' }
          : mobileNav
            ? isActive
              ? 'text'
              : 'textMuted'
            : isActive
              ? mobileSecondary
                ? 'text'
                : 'accent'
              : 'textMuted'
      }
      fontWeight={
        mobileNav
          ? isActive
            ? 'bold'
            : 'semibold'
          : isActive
            ? 'semibold'
            : mobileSecondary
              ? 'medium'
              : 'normal'
      }
      fontSize={mobileNav ? 'lg' : mobileSecondary ? 'md' : 'sm'}
      minH={mobileNav ? '54px' : mobileSecondary ? '48px' : '44px'}
      borderWidth={isMobile ? 0 : undefined}
      borderColor={isMobile ? 'transparent' : undefined}
      bg={isMobile ? 'transparent' : undefined}
      px={mobileNav ? 3 : undefined}
      py={mobileNav ? 2.5 : undefined}
      borderRadius={mobileNav ? 'lg' : undefined}
      _hover={
        danger
          ? {
              bg: 'red.500/10',
              borderColor: isMobile ? 'transparent' : 'red.500/20',
              color: { base: 'red.700', _dark: 'red.300' },
            }
          : undefined
      }
      _active={danger ? { bg: 'red.500/15' } : undefined}
      onClick={onClick}
      textAlign="left"
      textDecoration="none"
      aria-current={isActive ? 'page' : undefined}
      aria-label={
        badgeCount && badgeCount > 0
          ? `${label}, ${badgeCount} pending join request${badgeCount === 1 ? '' : 's'}`
          : undefined
      }
    >
      <Flex align="center" gap={mobileNav ? 3.5 : mobileSecondary ? 3 : 2.5} w="100%" minW={0}>
        <Box
          position="relative"
          flexShrink={0}
          w={mobileNav ? '28px' : undefined}
          display={mobileNav ? 'flex' : undefined}
          alignItems={mobileNav ? 'center' : undefined}
          justifyContent={mobileNav ? 'center' : undefined}
          fontSize={mobileNav ? '24px' : mobileSecondary ? '20px' : '1.05em'}
          lineHeight={1}
          color={
            mobileNav
              ? danger
                ? undefined
                : isActive
                  ? 'text'
                  : 'textMuted'
              : undefined
          }
          opacity={mobileNav ? 1 : isActive ? 1 : 0.75}
        >
          {renderedIcon}
          {badgeCount && badgeCount > 0 ? (
            <Box
              position="absolute"
              top={mobileNav ? '0px' : '-1px'}
              right={mobileNav ? '2px' : '-3px'}
              w="8px"
              h="8px"
              borderRadius="full"
              bg="accent"
              borderWidth="1.5px"
              borderColor={{ base: 'bgElevated', md: 'surface' }}
              pointerEvents="none"
              aria-hidden
            />
          ) : null}
        </Box>
        <Text flex={1} minW={0} truncate>
          {label}
        </Text>
        {shortcut && (
          <Box
            display={{ base: 'none', md: 'block' }}
            px={1.5}
            py={0.5}
            borderRadius="sm"
            bg="whiteAlpha.50"
            borderWidth="hairline"
            borderColor="border"
            fontSize="2xs"
            fontFamily="mono"
            color="textMuted"
            letterSpacing="normal"
            lineHeight={1.5}
            flexShrink={0}
          >
            {shortcut}
          </Box>
        )}
      </Flex>
    </RiftboundMenuItemButton>
  );

  if (mobileOnly || hideOnMobile) {
    return (
      <Box
        display={mobileOnly ? { base: 'block', md: 'none' } : { base: 'none', md: 'block' }}
        w="full"
      >
        {item}
      </Box>
    );
  }

  return item;
}

type AccountMenuTriggerProps = {
  slug: string;
  photoURL: string | null | undefined;
  pendingInviteCount: number;
  onClick?: ComponentPropsWithoutRef<'button'>['onClick'];
};

/** Must forward ref — desktop `Popover.Trigger asChild` anchors position to this node. */
const AccountMenuTrigger = forwardRef<HTMLButtonElement, AccountMenuTriggerProps>(
  function AccountMenuTrigger(
    { slug, photoURL, pendingInviteCount, onClick, ...rest },
    ref,
  ) {
    return (
      <Flex
        // Popover asChild merges button attrs + ref; Chakra Flex is typed as a div.
        ref={ref as never}
        as="button"
        align="center"
        gap={2}
        px={2}
        py={1.5}
        borderRadius="md"
        cursor="pointer"
        bg="transparent"
        outline="none"
        _hover={{ bg: { base: 'blackAlpha.50', _dark: 'whiteAlpha.100' } }}
        _focus={{ outline: 'none', boxShadow: 'none' }}
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'accent',
          outlineOffset: '2px',
        }}
        transition="background 0.1s"
        onClick={onClick as never}
        aria-label={
          pendingInviteCount > 0
            ? `Account menu, ${pendingInviteCount} pending team invite${pendingInviteCount === 1 ? '' : 's'}`
            : 'Account menu'
        }
        {...(rest as object)}
      >
        <Box position="relative" flexShrink={0}>
          <RiftboundPlayerAvatar
            name={slug}
            photoURL={photoURL}
            size="xs"
            colorPalette="purple"
          />
          {pendingInviteCount > 0 ? (
            <MobileFilterFabBadge count={pendingInviteCount} tone="notice" />
          ) : null}
        </Box>
        <SlugText
          slug={slug}
          fontSize="sm"
          display={{ base: 'none', md: 'block' }}
          color="text"
        />
      </Flex>
    );
  },
);

function AccountMenuHeader({
  slug,
  photoURL,
  pendingInviteCount,
}: {
  slug: string;
  photoURL: string | null | undefined;
  pendingInviteCount: number;
}) {
  return (
    <HStack gap={{ base: 3.5, md: 3 }} px={4} pt={1} pb={{ base: 2, md: 3 }} w="100%">
      <Box position="relative" flexShrink={0}>
        <Box display={{ base: 'block', md: 'none' }}>
          <RiftboundPlayerAvatar
            name={slug}
            photoURL={photoURL}
            size="lg"
            colorPalette="purple"
          />
        </Box>
        <Box display={{ base: 'none', md: 'block' }}>
          <RiftboundPlayerAvatar
            name={slug}
            photoURL={photoURL}
            size="md"
            colorPalette="purple"
          />
        </Box>
        {pendingInviteCount > 0 ? (
          <MobileFilterFabBadge count={pendingInviteCount} tone="notice" />
        ) : null}
      </Box>
      <Box minW={0}>
        <Text
          display={{ base: 'block', md: 'none' }}
          fontWeight="bold"
          fontSize="xl"
          color="text"
          lineHeight="1.2"
          lineClamp={1}
        >
          {slug}
        </Text>
        <SlugText
          slug={slug}
          fontWeight={{ base: 'normal', md: 'semibold' }}
          fontSize={{ base: 'sm', md: 'md' }}
          color={{ base: 'textMuted', md: 'text' }}
          lineClamp={1}
        />
      </Box>
    </HStack>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/** Close animation budget; fallback if `onCloseEnd` never fires. */
const SHEET_CLOSE_NAV_FALLBACK_MS = 420;

type PendingMenuAction =
  | { type: 'nav'; path: string }
  | { type: 'joinRequests' }
  | { type: 'signOut' }
  | { type: 'install' }
  | { type: 'androidRepair' };

function isModifiedClick(e: MouseEvent) {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

export function AccountMenuPopover() {
  const [open, setOpen] = useState(false);
  const [joinRequestsOpen, setJoinRequestsOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getDisplayImage, signOut } = useAuth();
  const totalCards = useCollectionSummary()?.totalCards ?? 0;
  const { decks } = useUserDecks({ enabled: open });
  const { binders } = useBinders({ enabled: open });
  const { team } = useUserTeam(user?.teamId);
  const { invites: pendingInvites } = usePendingInvites();
  const { count: pendingJoinRequestCount } = useAdminPendingJoinRequests();
  const { canInstall, isIos, isInstalled, needsAndroidRepair, install, dismiss } = useInstallPrompt();
  // Keep the menu item visible even when the banner is snoozed so users can still install manually
  const showInstall = !isInstalled && (canInstall || isIos);
  const showAndroidRepair = needsAndroidRepair;
  const binderCount =
    typeof user?.binderCount === 'number' ? user.binderCount : binders.length;
  const myTeamPath =
    pendingJoinRequestCount > 0 && team?.slug
      ? `/teams/${team.slug}?modal=members`
      : team?.slug
        ? `/teams/${team.slug}`
        : '/team';
  const pendingInviteCount = pendingInvites.length;
  const pendingActionRef = useRef<PendingMenuAction | null>(null);
  const closeFallbackTimerRef = useRef<number | null>(null);

  const photoURL = getDisplayImage();

  const clearCloseFallback = () => {
    if (closeFallbackTimerRef.current != null) {
      window.clearTimeout(closeFallbackTimerRef.current);
      closeFallbackTimerRef.current = null;
    }
  };

  const runPendingAction = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    clearCloseFallback();
    if (!action) return;

    switch (action.type) {
      case 'nav':
        startTransition(() => {
          navigate(action.path);
        });
        break;
      case 'joinRequests':
        setJoinRequestsOpen(true);
        break;
      case 'signOut':
        void signOut();
        break;
      case 'install':
        dismiss();
        if (canInstall) void install();
        break;
      case 'androidRepair':
        startTransition(() => {
          navigate('/about#faq');
        });
        break;
    }
  };

  const queueAction = (action: PendingMenuAction) => {
    pendingActionRef.current = action;
    setOpen(false);

    // Desktop popover dismisses instantly — run now. Mobile waits for sheet close.
    if (!isMobile) {
      runPendingAction();
      return;
    }

    clearCloseFallback();
    closeFallbackTimerRef.current = window.setTimeout(() => {
      runPendingAction();
    }, SHEET_CLOSE_NAV_FALLBACK_MS);
  };

  const go = (path: string) => {
    if (location.pathname === path) {
      setOpen(false);
      return;
    }
    queueAction({ type: 'nav', path });
  };

  const onNavItemClick = (path: string) => (e: MouseEvent) => {
    // Let the browser handle new-tab / modified clicks via RouterLink.
    if (isModifiedClick(e)) {
      setOpen(false);
      return;
    }
    e.preventDefault();
    go(path);
  };

  const close = () => {
    pendingActionRef.current = null;
    clearCloseFallback();
    setOpen(false);
  };

  const handleSheetCloseEnd = () => {
    runPendingAction();
  };

  useEffect(() => {
    return () => clearCloseFallback();
  }, []);

  // Keyboard shortcuts active while the menu is open (desktop popover).
  useEffect(() => {
    if (!open || isMobile) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      switch (e.key.toUpperCase()) {
        case 'P':
          e.preventDefault();
          go(playerProfilePath(user?.slug) ?? '/profile');
          break;
        case 'C':
          e.preventDefault();
          go('/collection');
          break;
        case 'B':
          e.preventDefault();
          go('/collection/binders');
          break;
        case 'D':
          e.preventDefault();
          go('/decks/my-decks');
          break;
        case 'H':
          e.preventDefault();
          go('/matches');
          break;
        case 'S':
          e.preventDefault();
          go('/profile');
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMobile, user?.slug, location.pathname]);

  if (!user) return null;

  const isOnHome = location.pathname === '/';
  const profilePath = playerProfilePath(user.slug) ?? '/profile';
  const isOnProfile = location.pathname === profilePath;
  const isOnCollection =
    location.pathname === '/collection' || location.pathname.startsWith('/collection/sets/');
  const isOnBinders =
    location.pathname === '/collection/binders' ||
    location.pathname.startsWith('/collection/binders/');
  const isOnDecks = location.pathname === '/decks' || location.pathname.startsWith('/decks/');
  const isOnMatchHistory = location.pathname.startsWith('/matches');
  const isOnSettings = location.pathname === '/profile' || location.pathname.startsWith('/profile/');
  const isOnTeam =
    location.pathname === '/team' ||
    location.pathname.startsWith('/teams/') ||
    location.pathname === '/features/teams';
  const isOnProxy = location.pathname === '/card-print';

  const menuBody = (
    <>
      <HStack
        gap={0}
        mx={3}
        mb={{ base: 2, md: 3 }}
        borderRadius="md"
        overflow="hidden"
        borderWidth="hairline"
        borderColor="border"
        bg={{ base: 'transparent', md: 'surface' }}
      >
        <StatBox label="Decks" value={decks.length} />
        <Box w="1px" bg="border" alignSelf="stretch" />
        <StatBox label="Binders" value={binderCount} />
        <Box w="1px" bg="border" alignSelf="stretch" />
        <StatBox label="Cards" value={totalCards} />
      </HStack>

      <Separator borderColor="border" />

      <VStack gap={{ base: 0.5, md: 1 }} p={{ base: 2.5, md: 2 }} align="stretch">
        {pendingInviteCount > 0 ? (
          <RiftboundMenuItemButton
            showCheck={false}
            as="button"
            cursor="pointer"
            color="bg"
            fontWeight="semibold"
            fontSize={{ base: 'md', md: 'sm' }}
            minH={{ base: '54px', md: '44px' }}
            bg="accent"
            borderColor="accent"
            _hover={{ bg: 'accentHover', borderColor: 'accentHover', color: 'bg' }}
            _active={{ bg: 'accentMuted', borderColor: 'accentMuted', color: 'bg' }}
            onClick={() => queueAction({ type: 'joinRequests' })}
            textAlign="left"
            textDecoration="none"
          >
            <Flex align="center" gap={{ base: 3.5, md: 2.5 }} w="100%" minW={0}>
              <Box flexShrink={0} fontSize={{ base: '22px', md: '1.05em' }} lineHeight={1}>
                <MdMailOutline />
              </Box>
              <Text flex={1} minW={0} truncate>
                Team invites ({pendingInviteCount})
              </Text>
            </Flex>
          </RiftboundMenuItemButton>
        ) : null}
        <MenuItem
          icon={<MdOutlineHome />}
          iconActive={<MdHome />}
          label="Home"
          isActive={isOnHome}
          to="/"
          onClick={onNavItemClick('/')}
          mobileOnly
        />
        <MenuItem
          icon={<MdOutlinePerson />}
          iconActive={<MdPerson />}
          label="Profile"
          shortcut="P"
          isActive={isOnProfile}
          to={profilePath}
          onClick={onNavItemClick(profilePath)}
        />
        <MenuItem
          icon={<MdOutlineGroups />}
          iconActive={<MdGroups />}
          label="Team"
          badgeCount={pendingJoinRequestCount}
          isActive={isOnTeam}
          to={myTeamPath}
          onClick={onNavItemClick(myTeamPath)}
        />
        <MenuItem
          icon={<MdOutlinePrint />}
          iconActive={<MdPrint />}
          label="Proxy"
          isActive={isOnProxy}
          to="/card-print"
          onClick={onNavItemClick('/card-print')}
          mobileOnly
        />
        <MenuItem
          icon={<MdOutlineCollections />}
          iconActive={<MdCollections />}
          label="Collection"
          shortcut="C"
          isActive={isOnCollection}
          to="/collection"
          onClick={onNavItemClick('/collection')}
          hideOnMobile
        />
        <MenuItem
          icon={<MdAutoStories />}
          label="Binders"
          shortcut="B"
          isActive={isOnBinders}
          to="/collection/binders"
          onClick={onNavItemClick('/collection/binders')}
          mobileOnly
        />
        <MenuItem
          icon={<DeckIcon width="1em" height="1em" />}
          label="My Decks"
          shortcut="D"
          isActive={isOnDecks}
          to="/decks/my-decks"
          onClick={onNavItemClick('/decks/my-decks')}
        />
        <MenuItem
          icon={<MdOutlineSportsScore />}
          iconActive={<MdSportsScore />}
          label="History"
          shortcut="H"
          isActive={isOnMatchHistory}
          to="/matches"
          onClick={onNavItemClick('/matches')}
        />
      </VStack>

      <Separator borderColor="border" />

      <VStack gap={{ base: 0.5, md: 1 }} p={{ base: 2.5, md: 2 }} pb={isMobile ? 4 : 2} align="stretch">
        <MenuItem
          tone="secondary"
          icon={<MdOutlineSettings />}
          iconActive={<MdSettings />}
          label="Settings"
          shortcut="S"
          isActive={isOnSettings}
          to="/profile"
          onClick={onNavItemClick('/profile')}
        />
        {showInstall ? (
          <MenuItem
            tone="secondary"
            icon={<MdDownload />}
            label={isIos ? 'Add to Home Screen' : 'Install app'}
            onClick={() => queueAction({ type: 'install' })}
          />
        ) : null}
        {showAndroidRepair ? (
          <MenuItem
            tone="secondary"
            icon={<MdPhonelinkSetup />}
            label="App won’t open?"
            onClick={() => queueAction({ type: 'androidRepair' })}
          />
        ) : null}
        <MenuItem
          tone="secondary"
          icon={<MdLogout />}
          label="Sign out"
          danger
          onClick={() => queueAction({ type: 'signOut' })}
        />
      </VStack>
    </>
  );

  const header = (
    <AccountMenuHeader
      slug={user.slug}
      photoURL={photoURL}
      pendingInviteCount={pendingInviteCount}
    />
  );

  return (
    <>
      {isMobile ? (
        <>
          <AccountMenuTrigger
            slug={user.slug}
            photoURL={photoURL}
            pendingInviteCount={pendingInviteCount}
            onClick={() => setOpen(true)}
          />
          <RiftboundBottomSheet
            isOpen={open}
            onClose={close}
            onCloseEnd={handleSheetCloseEnd}
            zIndex={SHEET_Z_FILTER}
            detent="content-height"
            // Menu rows are buttons — disable sheet drag so taps aren’t eaten as swipes.
            disableDrag
            header={header}
          >
            {menuBody}
          </RiftboundBottomSheet>
        </>
      ) : (
        <RiftboundPopover
          open={open}
          onOpenChange={setOpen}
          placement="bottom-end"
          width="240px"
          variant="elevated"
          bodyPadding={0}
          trigger={
            <AccountMenuTrigger
              slug={user.slug}
              photoURL={photoURL}
              pendingInviteCount={pendingInviteCount}
            />
          }
        >
          <Box pt={3}>{header}</Box>
          {menuBody}
        </RiftboundPopover>
      )}

      <JoinRequestsModal open={joinRequestsOpen} onClose={() => setJoinRequestsOpen(false)} />
    </>
  );
}
